import { Component, OnInit, OnDestroy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { User } from '../../_models/user';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { API_URLS } from '../../config/api.urls';
import { environment } from '../../../environments/environment';

@Component({
    selector: 'app-delivery-requests',
    templateUrl: './delivery-requests.component.html',
    styleUrls: ['./delivery-requests.component.css'],
    standalone: false
})
export class DeliveryRequestsComponent implements OnInit, OnDestroy {

    loading = true;
    titleKey = 'delivery_requests.title';
    user: User;
    requests: any[] = [];
    lang = 'fr';
    config: any = { page: 1, itemPerPage: 10, totalOfItems: 0, totalOfPage: 0, data: [] };

    private subscriptions: Subscription[] = [];

    constructor(
        private titleService: Title,
        private httpClient: HttpClient,
        private router: Router,
        private translateService: TranslateService,
        private toastr: ToastrService
    ) {
        this.user = JSON.parse(localStorage.getItem('currentUser') ?? '{}');
        this.lang = this.translateService.currentLang;

        const subs = this.translateService.get(this.titleKey).subscribe(value => {
            this.titleService.setTitle(value + ' - ' + environment.appTitle);
        });
        this.subscriptions.push(subs);
    }

    ngOnInit() {
        this.loadRequests();
    }

    loadRequests(page = 1) {
        this.loading = true;
        const sub = this.httpClient.get<any>(API_URLS.DELIVERY_REQUESTS_BASE_URL + '?page=' + page)
            .subscribe({
                next: (res: any) => {
                    const data = res.data;
                    this.requests = data.data || [];
                    this.config = {
                        page: data.page,
                        itemPerPage: data.itemPerPage,
                        totalOfItems: data.totalOfItems,
                        totalOfPage: data.totalOfPage,
                        data: this.requests
                    };
                    this.loading = false;
                },
                error: () => {
                    this.loading = false;
                    this.toastr.error('Failed to load delivery requests');
                }
            });
        this.subscriptions.push(sub);
    }

    pageChange(page: number) {
        window.scrollTo(0, 0);
        this.loadRequests(page);
    }

    viewDetail(request: any) {
        this.router.navigate(['/user', 'delivery-requests', request.id]);
    }

    getStatusClass(status: string): string {
        switch (status?.toUpperCase()) {
            case 'PENDING': return 'badge bg-warning text-dark';
            case 'CONFIRMED': return 'badge bg-primary';
            case 'IN_PREPARATION': return 'badge bg-info';
            case 'IN_TRANSIT': return 'badge bg-purple';
            case 'IN_DELIVERING': return 'badge bg-primary';
            case 'DELIVERED': return 'badge bg-success';
            case 'FAILED': return 'badge bg-danger';
            default: return 'badge bg-secondary';
        }
    }

    getDeliveryStatusLabel(status: string): string {
        if (!status) return '';
        return 'delivery.status_' + status.toLowerCase();
    }

    formatDate(dateStr: string): string {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const options: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric', year: 'numeric' };
        return date.toLocaleDateString(this.lang === 'fr' ? 'fr-FR' : 'en-US', options);
    }

    formatCurrency(amount: number): string {
        if (amount == null) return '';
        return new Intl.NumberFormat(this.lang === 'fr' ? 'fr-FR' : 'en-US').format(amount) + ' XAF';
    }

    getProductName(req: any): string {
        if (!req.product) return '';
        return this.lang === 'fr' && req.product.productNameFr
            ? req.product.productNameFr
            : req.product.productName;
    }

    productPictureUrl(req: any): string {
        const picture = req.product?.picture || req.product?.defaultPicture;
        if (!picture) return '';
        return environment.image_cdn_url + '/' + picture;
    }

    pickupAddressString(req: any): string {
        const addr = req.pickupAddress;
        if (!addr) return '';
        return [addr.addressLine1, addr.addressLine2, addr.town, addr.country]
            .filter((v: string) => v).join(', ');
    }

    deliveryAddressString(req: any): string {
        const addr = req.deliveryAddress;
        if (!addr) return '';
        return [addr.addressLine1, addr.addressLine2, addr.town, addr.country?.name || addr.country]
            .filter((v: string) => v).join(', ');
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

}
